import { useState } from "react";
import { Clock, AlertCircle, FileText, ChevronDown, ChevronRight, Repeat, Zap, Shield, AlertTriangle, RefreshCw, MapPin } from "lucide-react";
import { Plus, X } from "lucide-react";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { apiFetch } from "../../../../context/AuthContext";
import PermissionInput from "../ui/PermissionInput";
import ToggleSwitch from "../ui/ToggleSwitch";
import { PALETTE } from "../../styles/configStyles";
import EffectiveDateBadge from "../EffectiveDateBadge";

// ─── Single blue color for all tiers (matches old file style) ───────────────
const RULE_COLOR = {
  border: PALETTE.primary?.base ?? "#2e7cba",
  bg: `rgba(46,124,186,0.07)`,
  badge: `rgba(46,124,186,0.15)`,
  text: PALETTE.primary?.base ?? "#2e7cba",
};

const RECURRENCE_ACTIONS = [
  { value: "none", labelEn: "No special recurrence", labelAr: "بدون تصرف خاص" },
  { value: "escalate", labelEn: "Escalate to next tier", labelAr: "تصعيد للمستوى التالي" },
  { value: "fixed_penalty", labelEn: "Fixed penalty amount", labelAr: "غرامة ثابتة" },
  { value: "warning_then_deduct", labelEn: "Warning, then deduct", labelAr: "تحذير ثم خصم" },
];

// ─── Suppress number input spinners globally for this component ──────────────
const NO_SPINNER_STYLE = `
  .attendance-tab-no-spinner::-webkit-inner-spin-button,
  .attendance-tab-no-spinner::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  .attendance-tab-no-spinner { -moz-appearance: textfield; appearance: textfield; }
`;

// Spread onto every <input type="number"> to remove spinners and prevent scroll-wheel changes
const noSpinner = {
  className: "attendance-tab-no-spinner",
  onWheel: (e) => e.target.blur(),
};

const AttendanceTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
  // Admin-level feature gate (registry: configurations.manageDepartmentRules)
  canManageDepartmentRules = true,
  isCreatingNewConfig,
  isEditingArchive,
  setIsEditingArchive,
  selectedArchiveId,
  setSelectedArchiveId,
  archivedConfigs,
  selectedCompanyName,
  selectArchive,
  departments,
  loadingDepartments,
  loading,
  setLoading,
  loadConfiguration,
  updateArchive,
  loadArchives,
  BASE_URL,
  profile,
  showAlert,
  unlockTab,
  resetTabToGlobal,
  getTabEffectiveDate,
  getBadgeMode,
}) => {
  const canEdit = canEditField("attendance");
  const [expandedAdvanced, setExpandedAdvanced] = useState({});

  const departmentRules =
    config && typeof config.departmentRules === "object" && config.departmentRules !== null
      ? config.departmentRules
      : {};

  const getAttendanceValue = (field) => {
    if (
      config.department &&
      departmentRules &&
      typeof departmentRules === "object" &&
      Object.prototype.hasOwnProperty.call(departmentRules, config.department) &&
      departmentRules[config.department]
    ) {
      const deptRules = departmentRules[config.department];
      return deptRules && deptRules[field] !== undefined ? deptRules[field] : config[field];
    }
    return config[field];
  };

  const handleAttendanceChange = (field, value) => {
    if (config.department) {
      setConfig((prev) => ({
        ...prev,
        departmentRules: {
          ...prev.departmentRules,
          [prev.department]: {
            ...(prev.departmentRules[prev.department] || {}),
            [field]: value,
          },
        },
      }));
    } else {
      setConfig((prev) => ({ ...prev, [field]: value }));
    }
  };

  // ── Working Company default locations ─────────────────────────────────────
  // Company-scoped only: these always write to the company config, never into
  // departmentRules. There is deliberately no per-department override in v1
  // (see the plan) — hence a dedicated setter rather than
  // `handleAttendanceChange`, which redirects to the selected department.
  const companyLocations = Array.isArray(config.defaultLocations)
    ? config.defaultLocations
    : [];
  const editingDepartment = Boolean(config.department);
  const canEditCompanyLocations = canEdit && !editingDepartment;

  const setCompanyLocations = (next) =>
    setConfig((prev) => ({ ...prev, defaultLocations: next }));

  const updateCompanyLocation = (index, field, value) =>
    setCompanyLocations(
      companyLocations.map((loc, i) => (i === index ? { ...loc, [field]: value } : loc)),
    );

  const addCompanyLocation = () =>
    setCompanyLocations([...companyLocations, { name: "", mapUrl: "", radius: 500 }]);

  const removeCompanyLocation = (index) =>
    setCompanyLocations(companyLocations.filter((_, i) => i !== index));

  // Styles for the locations editor. Kept together so the RTL rules stay
  // consistent: the container flips with the page, but map links, coordinates
  // and the radius are always left-to-right — they are not prose, and mirroring
  // them makes a URL unreadable and a coordinate pair look transposed.
  const isDark = theme === "dark";
  const locStyles = {
    hint: {
      fontSize: 12,
      lineHeight: 1.7,
      opacity: 0.72,
      color: isDark ? "rgba(226,232,240,0.85)" : "rgba(15,23,42,0.75)",
    },
    countBadge: {
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1,
      padding: "3px 7px",
      borderRadius: 999,
      color: PALETTE.blue.base,
      background: isDark ? "rgba(59,130,246,0.16)" : "rgba(59,130,246,0.10)",
      border: `1px solid ${isDark ? "rgba(59,130,246,0.35)" : "rgba(59,130,246,0.25)"}`,
    },
    notice: {
      marginTop: 10,
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12.5,
      lineHeight: 1.7,
      padding: "9px 12px",
      borderRadius: 8,
      color: isDark ? "#fbbf24" : "#b45309",
      background: isDark ? "rgba(251,191,36,0.10)" : "rgba(245,158,11,0.08)",
      border: `1px solid ${isDark ? "rgba(251,191,36,0.28)" : "rgba(245,158,11,0.25)"}`,
      direction: isRTL ? "rtl" : "ltr",
    },
    list: {
      marginTop: 14,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      direction: isRTL ? "rtl" : "ltr",
    },
    empty: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 12.5,
      lineHeight: 1.7,
      padding: "16px 14px",
      borderRadius: 10,
      color: isDark ? "rgba(226,232,240,0.65)" : "rgba(15,23,42,0.55)",
      background: isDark ? "rgba(51,65,85,0.25)" : "rgba(248,250,252,0.9)",
      border: `1px dashed ${isDark ? "rgba(148,163,184,0.25)" : "rgba(148,163,184,0.35)"}`,
    },
    card: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: "14px 16px",
      borderRadius: 10,
      background: isDark ? "rgba(51,65,85,0.28)" : "rgba(248,250,252,0.95)",
      border: `1px solid ${isDark ? "rgba(148,163,184,0.16)" : "rgba(226,232,240,0.95)"}`,
      borderInlineStart: `3px solid ${PALETTE.blue.base}`,
    },
    cardTop: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 10,
      alignItems: "end",
    },
    cardBottom: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 140px",
      gap: 10,
      alignItems: "end",
    },
    fieldLabel: {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: "0.02em",
      color: isDark ? "rgba(226,232,240,0.7)" : "rgba(15,23,42,0.6)",
    },
    textInput: {
      direction: isRTL ? "rtl" : "ltr",
      textAlign: isRTL ? "right" : "left",
    },
    // URLs, coordinates and metre counts read left-to-right in every locale.
    ltrInput: { direction: "ltr", textAlign: "left" },
    coords: {
      fontSize: 11.5,
      fontFamily: "ui-monospace, 'Cascadia Mono', Consolas, monospace",
      color: isDark ? "rgba(148,163,184,0.85)" : "rgba(100,116,139,0.95)",
      direction: "ltr",
      textAlign: isRTL ? "right" : "left",
    },
    pending: {
      fontFamily: "inherit",
      color: isDark ? "#fbbf24" : "#b45309",
      direction: isRTL ? "rtl" : "ltr",
      display: "inline-block",
    },
    removeBtn: (enabled) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 38,
      height: 38,
      padding: 0,
      borderRadius: 8,
      cursor: enabled ? "pointer" : "not-allowed",
      opacity: enabled ? 1 : 0.4,
      color: isDark ? "#f87171" : "#dc2626",
      background: "transparent",
      border: `1px solid ${isDark ? "rgba(248,113,113,0.30)" : "rgba(220,38,38,0.22)"}`,
    }),
    addBtn: {
      alignSelf: "flex-start",
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "9px 15px",
      fontSize: 13,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      borderRadius: 8,
      color: PALETTE.blue.base,
      background: isDark ? "rgba(59,130,246,0.10)" : "rgba(59,130,246,0.06)",
      border: `1px solid ${isDark ? "rgba(59,130,246,0.35)" : "rgba(59,130,246,0.28)"}`,
    },
  };

  const handleDepartmentChange = async (selectedDept) => {
    console.log(`📋 [AttendanceTab] handleDepartmentChange called with:`, selectedDept);
    if (!selectedDept) {
      if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
        const selectedArchive = archivedConfigs.find((a) => a.id === selectedArchiveId);
        if (selectedArchive) {
          setConfig((prev) => ({
            ...prev,
            department: "",
            departmentRules:
              prev.departmentRules && typeof prev.departmentRules === "object" && Object.keys(prev.departmentRules).length > 0
                ? prev.departmentRules
                : (() => {
                    if (!selectedArchive.attendanceRules) return {};
                    return typeof selectedArchive.attendanceRules === "string"
                      ? JSON.parse(selectedArchive.attendanceRules)
                      : selectedArchive.attendanceRules;
                  })(),
          }));
        }
      } else {
        setConfig((prev) => ({ ...prev, department: "" }));
        if (selectedCompanyName) {
          setLoading(true);
          try {
            await loadConfiguration(selectedCompanyName);
          } catch (e) {
            console.error("Failed to reload company-wide config", e);
          } finally {
            setLoading(false);
          }
        }
      }
      return;
    }

    if (isEditingArchive && selectedArchiveId && archivedConfigs.length > 0) {
      const selectedArchive = archivedConfigs.find((a) => a.id === selectedArchiveId);

      setConfig((prev) => {
        const currentDeptRules =
          prev.departmentRules && typeof prev.departmentRules === "object" && Object.keys(prev.departmentRules).length > 0
            ? prev.departmentRules
            : (() => {
                if (!selectedArchive?.attendanceRules) return {};
                return typeof selectedArchive.attendanceRules === "string"
                  ? JSON.parse(selectedArchive.attendanceRules)
                  : selectedArchive.attendanceRules;
              })();

        const archiveDeptRules = currentDeptRules[selectedDept];

        if (archiveDeptRules) {
          console.log(`✅ [Archive mode] Found dept rules for "${selectedDept}" in archive ${selectedArchiveId}`);
          return {
            ...prev,
            department: selectedDept,
            departmentRules: currentDeptRules,
            workStartTime: archiveDeptRules.workStartTime || selectedArchive?.workStartTime || prev.workStartTime,
            workEndTime: archiveDeptRules.workEndTime || selectedArchive?.workEndTime || prev.workEndTime,
            isOvernightShift:
              archiveDeptRules.isOvernightShift !== undefined
                ? archiveDeptRules.isOvernightShift
                : selectedArchive?.isOvernightShift ?? false,
            shiftDayPivotHour:
              archiveDeptRules.shiftDayPivotHour !== undefined
                ? archiveDeptRules.shiftDayPivotHour
                : selectedArchive?.shiftDayPivotHour ?? null,
            workingHoursPerDay: archiveDeptRules.workingHoursPerDay ?? selectedArchive?.workingHoursPerDay,
            workingDaysPerWeek: archiveDeptRules.workingDaysPerWeek ?? selectedArchive?.workingDaysPerWeek,
            absenceDeductionPerDay: archiveDeptRules.absenceDeductionPerDay ?? selectedArchive?.absenceDeductionPerDay,
            lateDeductionRules: archiveDeptRules.lateDeductionRules ?? selectedArchive?.lateDeductionRules,
            overtimeEnabled: archiveDeptRules.overtimeEnabled ?? selectedArchive?.overtimeEnabled,
            overtimeRate: archiveDeptRules.overtimeRate ?? selectedArchive?.overtimeRate,
            overtimeStartAfter: archiveDeptRules.overtimeStartAfter ?? selectedArchive?.overtimeStartAfter,
            maxOvertimeHoursPerDay: archiveDeptRules.maxOvertimeHoursPerDay ?? selectedArchive?.maxOvertimeHoursPerDay,
            weekendDays: archiveDeptRules.weekendDays || selectedArchive?.weekendDays,
            halfDayRequestThreshold: archiveDeptRules.halfDayRequestThreshold ?? selectedArchive?.halfDayRequestThreshold,
            allowDefaultLocation: archiveDeptRules.allowDefaultLocation ?? selectedArchive?.allowDefaultLocation ?? true,
          };
        } else {
          console.log(`ℹ️ [Archive mode] No dept rules for "${selectedDept}" in archive ${selectedArchiveId}, using archive company-wide config`);
          return {
            ...prev,
            department: selectedDept,
            departmentRules: currentDeptRules,
          };
        }
      });
      return;
    }

    const departmentRulesMap =
      config && typeof config.departmentRules === "object" && config.departmentRules !== null
        ? config.departmentRules
        : {};

    if (departmentRulesMap[selectedDept]) {
      console.log(`✅ [AttendanceTab] Found department rules for "${selectedDept}" in local cache`);
      setConfig((prev) => ({
        ...prev,
        department: selectedDept,
      }));
    } else {
      try {
        const clientName = profile?.clientName || localStorage.getItem("clientName");
        const response = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(selectedCompanyName)}/departments/${encodeURIComponent(selectedDept)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.rules) {
            const deptRules = data.data.rules;
            console.log(`✅ [AttendanceTab] Fetched department rules for "${selectedDept}" from API`);
            setConfig((prev) => ({
              ...prev,
              department: selectedDept,
              departmentRules: { ...prev.departmentRules, [selectedDept]: deptRules },
            }));
          } else {
            console.log(`ℹ️ [AttendanceTab] No department rules for "${selectedDept}" on server, using company-wide`);
            setConfig((prev) => ({
              ...prev,
              department: selectedDept,
            }));
          }
        } else {
          setConfig((prev) => ({
            ...prev,
            department: selectedDept,
          }));
        }
      } catch (error) {
        console.error("❌ Error loading rules for department:", error);
        setConfig((prev) => ({
          ...prev,
          department: selectedDept,
        }));
      }
    }
  };

  const fmtDate = (v) => {
    if (!v) return "—";
    try {
      return new Date(v).toISOString().split("T")[0];
    } catch {
      return "—";
    }
  };

  const selectedArchive = archivedConfigs.find((a) => a.id === selectedArchiveId) || null;

  // ── Advanced panel helpers ────────────────────────────────────────────────
  const ruleHasAdvanced = (rule) =>
    (rule.deductionMultiplier !== undefined && rule.deductionMultiplier !== 1) ||
    (rule.recurrenceAction && rule.recurrenceAction !== "none") ||
    (rule.recurrenceDeductions && rule.recurrenceDeductions.some((v) => v !== null && v !== undefined)) ||
    (rule.recurrenceResetMonths !== undefined && rule.recurrenceResetMonths !== null);

  const toggleAdvanced = (idx) => {
    setExpandedAdvanced((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const isAdvancedExpanded = (idx, rule) =>
    expandedAdvanced[idx] !== undefined ? expandedAdvanced[idx] : ruleHasAdvanced(rule);

  // ── Shared input style ────────────────────────────────────────────────────
  const blueAccent = PALETTE.primary?.base ?? "#2e7cba";

  return (
    <>
      {/* ── No-spinner CSS injection ────────────────────────────────────────── */}
      <style>{NO_SPINNER_STYLE}</style>

      {/* ── Archive Picker Bar ──────────────────────────────────────────────── */}
      <div style={{
        ...styles.contentSection,
        marginBottom: 16,
        padding: "14px 20px",
        background: isEditingArchive
          ? theme === "dark" ? "rgba(245,158,11,0.09)" : "rgba(255,251,235,1)"
          : styles.contentSection.background,
        border: isEditingArchive ? "2px solid #f59e0b" : styles.contentSection.border,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <FileText size={17} color="#f59e0b" />
          <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", color: theme === "dark" ? "#fbbf24" : "#92400e" }}>
            {isRTL ? "التاريخ:" : "History:"}
          </span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <ReusableDropdown
              items={archivedConfigs}
              value={selectedArchiveId ? String(selectedArchiveId) : ""}
              getItemKey={(item) => String(item.id)}
              getItemLabel={(item) => {
                const d = fmtDate(item.attendanceEffectiveDate || item.effectiveDate);
                return d + (item.reason ? "  •  " + item.reason : "") + "  (ID: " + item.id + ")";
              }}
              renderItem={(item) => {
                const d = fmtDate(item.attendanceEffectiveDate || item.effectiveDate);
                return (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{d}</div>
                    {item.reason && (
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{item.reason}</div>
                    )}
                    <div style={{ fontSize: 11, opacity: 0.5, marginTop: 1 }}>ID: {item.id}</div>
                  </div>
                );
              }}
              onChange={(id) => {
                if (!id) { setSelectedArchiveId(null); setIsEditingArchive(false); return; }
                const archive = archivedConfigs.find((a) => String(a.id) === String(id));
                if (archive) selectArchive(archive);
              }}
              onClear={() => { setSelectedArchiveId(null); setIsEditingArchive(false); }}
              placeholder={isRTL ? "— التكوين الحالي (بدون أرشيف) —" : "— Live configuration (current) —"}
              theme={theme}
              language={language}
              showAllOnArrowClick={true}
              showClear={!!selectedArchiveId}
              inputStyle={{
                borderRadius: 8,
                border: isEditingArchive ? "1.5px solid #f59e0b" : "1px solid rgba(148,163,184,0.3)",
                fontSize: 13,
                padding: "7px 40px 7px 12px",
                background: isEditingArchive
                  ? theme === "dark" ? "rgba(245,158,11,0.08)" : "rgba(255,251,235,0.8)"
                  : theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
                color: theme === "dark" ? "#f1f5f9" : "#334155",
              }}
            />
          </div>
          <button
            onClick={() => loadArchives(selectedCompanyName)}
            style={{ padding: "7px 13px", borderRadius: 8, border: "1px solid #ccc", background: "transparent", cursor: "pointer", fontSize: 14, color: theme === "dark" ? "#f0f0f0" : "#555" }}
          >
            {isRTL ? "تحديث" : "Refresh"}
          </button>
        </div>

        {isEditingArchive && selectedArchive && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#d97706", fontWeight: 600 }}>
              {isRTL
                ? "وضع تعديل الأرشيف — التغييرات تُحفظ في الأرشيف فقط وليس في التكوين الحالي"
                : "Archive edit mode — changes save to archive only, not to live config"}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: "rgba(245,158,11,0.18)", color: "#d97706" }}>
              {isRTL ? "تاريخ السريان:" : "Effective:"} {fmtDate(selectedArchive.attendanceEffectiveDate || selectedArchive.effectiveDate)}
            </span>
            <button
              onClick={() => updateArchive(selectedArchiveId)}
              disabled={loading}
              style={{ padding: "6px 16px", borderRadius: 8, border: "none", background: "#f59e0b", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              {loading ? (isRTL ? "جار الحفظ…" : "Saving…") : (isRTL ? "حفظ في الأرشيف" : "Save to Archive")}
            </button>
            <button
              onClick={() => { setIsEditingArchive(false); setSelectedArchiveId(null); }}
              style={{ padding: "6px 16px", borderRadius: 8, border: "1.5px solid #ccc", background: "transparent", fontWeight: 700, fontSize: 13, cursor: "pointer", color: theme === "dark" ? "#f0f0f0" : "#555" }}
            >
              {isRTL ? "خروج من الأرشيف" : "Exit Archive"}
            </button>
          </div>
        )}
      </div>

      {/* ── Attendance Rules ────────────────────────────────────────────────── */}
      <div style={{ ...styles.contentSection, border: styles.contentSection.border, boxShadow: styles.contentSection.boxShadow }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
            <div style={styles.sectionIconBadge(PALETTE.blue.base)}>
              <Clock size={20} />
            </div>
            {t.attendance.title}
            {isEditingArchive && (
              <span style={{ fontSize: "12px", fontWeight: 800, marginLeft: "12px", padding: "4px 12px", borderRadius: "6px", background: "rgba(245,158,11,0.18)", color: "#d97706" }}>
                {isRTL ? "التاريخ" : "HISTORY"}
              </span>
            )}
            {!canEdit && (
              <span style={{
                fontSize: "12px", fontWeight: "normal", marginLeft: "12px", padding: "4px 12px", borderRadius: "6px",
                background: theme === "dark" ? "rgba(251,191,36,0.15)" : "rgba(251,191,36,0.1)",
                color: theme === "dark" ? "#fbbf24" : "#d97706",
              }}>
                {language === "ar" ? "(للقراءة فقط)" : "(Read-only)"}
              </span>
            )}
          </h3>
          <EffectiveDateBadge
            date={getTabEffectiveDate ? getTabEffectiveDate("attendance") : config.attendanceEffectiveDate}
            isRTL={isRTL}
            theme={theme}
            mode={getBadgeMode ? getBadgeMode("attendance") : "locked"}
            onUnlock={unlockTab ? () => unlockTab("attendance") : undefined}
            onReset={resetTabToGlobal ? () => resetTabToGlobal("attendance") : undefined}
            onDateChange={(date) => setConfig((prev) => ({ ...prev, attendanceEffectiveDate: date }))}
          />
        </div>

        {/* Department Selector — hidden without configurations.manageDepartmentRules */}
        {canManageDepartmentRules && (
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              {isRTL ? "القسم" : "Department"}
              {config.department && (
                <span style={{ marginLeft: isRTL ? "0" : "10px", marginRight: isRTL ? "10px" : "0", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "800", background: "rgba(46,124,186,0.15)", color: "#2e7cba", textTransform: "uppercase" }}>
                  {isRTL ? "قواعد خاصة" : "Dept Rules"}
                </span>
              )}
            </label>
            <ReusableDropdown
              items={[
                { value: "", label: isRTL ? "--- إعدادات الشركة العامة ---" : "--- Company-Wide Settings ---" },
                ...departments.map((dept) => ({ value: dept, label: dept + (config.departmentRules && config.departmentRules[dept] ? " ✓" : "") }))
              ]}
              value={config.department || ""}
              onChange={(value) => handleDepartmentChange(value)}
              onSelect={(item) => handleDepartmentChange(item.value)}
              placeholder={loadingDepartments ? (isRTL ? "جاري التحميل..." : "Loading departments...") : departments.length === 0 ? (isRTL ? "لا توجد أقسام" : "No departments found") : (isRTL ? "اختر قسم (اختياري)" : "Select Department (Optional)")}
              theme={theme}
              language={language}
              disabled={loadingDepartments || !selectedCompanyName}
              getItemKey={(item) => item.value}
              getItemLabel={(item) => item.label}
              maxItems={20}
            />
            <p style={{ fontSize: "12px", color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.6)", marginTop: "6px", fontStyle: "italic" }}>
              {config.department
                ? isRTL ? `تعديل القواعد الخاصة بقسم: ${config.department}` : `Editing rules specific to: ${config.department}`
                : isRTL ? "تعديل القواعد العامة لجميع الأقسام" : "Editing company-wide rules for all departments"}
            </p>
          </div>
        </div>
        )}

        {/* Working Hours */}
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.workStartTime}</label>
            <input type="time" value={getAttendanceValue("workStartTime")} onChange={(e) => canEdit && handleAttendanceChange("workStartTime", e.target.value)} disabled={!canEdit} style={getFieldStyle("attendance", styles.input)} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.workEndTime}</label>
            <input type="time" value={getAttendanceValue("workEndTime")} onChange={(e) => canEdit && handleAttendanceChange("workEndTime", e.target.value)} disabled={!canEdit} style={getFieldStyle("attendance", styles.input)} />
          </div>
          {/* Overnight shift toggle */}
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <div style={{ ...styles.toggleContainer, marginBottom: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>{t.attendance.isOvernightShift}</label>
                <span style={{ fontSize: 12, opacity: 0.7, color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)" }}>
                  {t.attendance.isOvernightShiftHelp}
                </span>
              </div>
              <ToggleSwitch
                checked={Boolean(getAttendanceValue("isOvernightShift"))}
                onChange={(e) => canEdit && handleAttendanceChange("isOvernightShift", e.target.checked)}
                disabled={!canEdit}
                theme={theme}
                accentColor={PALETTE.blue.base}
                isRTL={isRTL}
              />
            </div>
          </div>
          {/* Allow Default Location toggle */}
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <div style={{ ...styles.toggleContainer, marginBottom: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>
                  {isRTL ? "تمكين الموقع الافتراضي للموظف" : "Enable Default Location For Employee"}
                </label>
                <span style={{ fontSize: 12, opacity: 0.7, color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)" }}>
                  {isRTL
                    ? "عند التفعيل، يمكنك تعيين موقع افتراضي للموظفين وإضافته في نافذة الموقع الافتراضي."
                    : "When enabled, default locations can be assigned to employees in the Default Location modal."}
                </span>
              </div>
              <ToggleSwitch
                checked={Boolean(getAttendanceValue("allowDefaultLocation") ?? true)}
                onChange={(e) => canEdit && handleAttendanceChange("allowDefaultLocation", e.target.checked)}
                disabled={!canEdit}
                theme={theme}
                accentColor={PALETTE.blue.base}
                isRTL={isRTL}
              />
            </div>
          </div>

          {/* ── Working Company default locations ───────────────────────── */}
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <div style={{ ...styles.toggleContainer, marginBottom: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>
                  <MapPin size={15} color={PALETTE.blue.base} />
                  {isRTL ? "المواقع الافتراضية لشركة العمل" : "Working Company Default Locations"}
                  {companyLocations.length > 0 && (
                    <span style={locStyles.countBadge}>{companyLocations.length}</span>
                  )}
                </label>
                <span style={locStyles.hint}>
                  {isRTL
                    ? "كل موظف في شركة العمل هذه يستطيع تسجيل الحضور من هذه المواقع، بالإضافة إلى أي موقع خاص به."
                    : "Every employee in this working company can punch from these locations, in addition to any of their own."}
                </span>
              </div>
              <ToggleSwitch
                checked={Boolean(config.allowCompanyDefaultLocation ?? true)}
                onChange={(e) =>
                  canEditCompanyLocations &&
                  setConfig((prev) => ({ ...prev, allowCompanyDefaultLocation: e.target.checked }))
                }
                disabled={!canEditCompanyLocations}
                theme={theme}
                accentColor={PALETTE.blue.base}
                isRTL={isRTL}
              />
            </div>

            {editingDepartment && (
              <div style={locStyles.notice}>
                <AlertCircle size={15} style={{ flexShrink: 0 }} />
                <span>
                  {isRTL
                    ? "هذه المواقع تُحدَّد على مستوى شركة العمل فقط. اختر «كل الأقسام» لتعديلها."
                    : "These locations are set per working company. Switch to All Departments to edit them."}
                </span>
              </div>
            )}

            {Boolean(config.allowCompanyDefaultLocation ?? true) && (
              <div style={locStyles.list}>
                {companyLocations.length === 0 ? (
                  <div style={locStyles.empty}>
                    <MapPin size={20} style={{ opacity: 0.45, flexShrink: 0 }} />
                    <span>
                      {isRTL
                        ? "لا توجد مواقع للشركة بعد. الموظفون يستخدمون مواقعهم الخاصة فقط."
                        : "No company locations yet. Employees use only their own locations."}
                    </span>
                  </div>
                ) : (
                  companyLocations.map((loc, index) => {
                    const lat = Number(loc.latitude);
                    const lng = Number(loc.longitude);
                    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
                    return (
                      <div key={index} style={locStyles.card}>
                        <div style={locStyles.cardTop}>
                          <div style={styles.formGroup}>
                            <label style={locStyles.fieldLabel}>
                              {isRTL ? "اسم الموقع" : "Location name"}
                            </label>
                            <input
                              type="text"
                              disabled={!canEditCompanyLocations}
                              placeholder={isRTL ? "مثال: المقر الرئيسي" : "e.g. Head Office"}
                              value={loc.name ?? ""}
                              onChange={(e) => updateCompanyLocation(index, "name", e.target.value)}
                              style={{ ...getFieldStyle("attendance", styles.input), ...locStyles.textInput }}
                            />
                          </div>
                          <button
                            type="button"
                            disabled={!canEditCompanyLocations}
                            onClick={() => removeCompanyLocation(index)}
                            title={isRTL ? "حذف الموقع" : "Remove location"}
                            aria-label={isRTL ? "حذف الموقع" : "Remove location"}
                            style={locStyles.removeBtn(canEditCompanyLocations)}
                          >
                            <X size={15} />
                          </button>
                        </div>

                        <div style={locStyles.cardBottom}>
                          <div style={styles.formGroup}>
                            <label style={locStyles.fieldLabel}>
                              {isRTL ? "رابط خرائط جوجل" : "Google Maps link"}
                            </label>
                            <input
                              type="text"
                              dir="ltr"
                              disabled={!canEditCompanyLocations}
                              placeholder={
                                hasCoords
                                  ? (isRTL ? "الصق رابطًا للتغيير" : "Paste a link to change")
                                  : "https://maps.google.com/..."
                              }
                              value={loc.mapUrl ?? ""}
                              onChange={(e) => updateCompanyLocation(index, "mapUrl", e.target.value)}
                              style={{ ...getFieldStyle("attendance", styles.input), ...locStyles.ltrInput }}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={locStyles.fieldLabel}>
                              {isRTL ? "النطاق (متر)" : "Radius (m)"}
                            </label>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              dir="ltr"
                              disabled={!canEditCompanyLocations}
                              value={loc.radius ?? 500}
                              onChange={(e) => updateCompanyLocation(index, "radius", Number(e.target.value))}
                              {...noSpinner}
                              style={{ ...getFieldStyle("attendance", styles.input), ...locStyles.ltrInput }}
                            />
                          </div>
                        </div>

                        <div style={locStyles.coords}>
                          {hasCoords ? (
                            <span dir="ltr">{lat.toFixed(6)}, {lng.toFixed(6)}</span>
                          ) : (
                            <span style={locStyles.pending}>
                              {isRTL
                                ? "لم تُحدَّد إحداثيات بعد — أضف رابط خرائط قبل الحفظ."
                                : "No coordinates yet — add a maps link before saving."}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}

                {canEditCompanyLocations && (
                  <button type="button" onClick={addCompanyLocation} style={locStyles.addBtn}>
                    <Plus size={15} />
                    {isRTL ? "إضافة موقع" : "Add location"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pivot hour */}
          {Boolean(getAttendanceValue("isOvernightShift")) && (
            <div style={styles.formGroup}>
              <label style={styles.label}>{t.attendance.shiftDayPivotHour}</label>
              <input
                type="number" min={0} max={23} step={1} disabled={!canEdit} placeholder="auto"
                value={getAttendanceValue("shiftDayPivotHour") === null || getAttendanceValue("shiftDayPivotHour") === undefined ? "" : getAttendanceValue("shiftDayPivotHour")}
                onChange={(e) => {
                  if (!canEdit) return;
                  const raw = e.target.value;
                  if (raw === "") { handleAttendanceChange("shiftDayPivotHour", null); return; }
                  const n = Number(raw);
                  if (Number.isInteger(n) && n >= 0 && n <= 23) handleAttendanceChange("shiftDayPivotHour", n);
                }}
                {...noSpinner}
                style={getFieldStyle("attendance", styles.input)}
              />
              <span style={{ fontSize: 12, opacity: 0.7 }}>{t.attendance.shiftDayPivotHourHelp}</span>
            </div>
          )}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.workingHoursPerDay}</label>
            <input type="number" min="1" max="24" value={getAttendanceValue("workingHoursPerDay")} onChange={(e) => { if (!canEdit) return; const parsed = parseInt(e.target.value, 10); if (!isNaN(parsed)) handleAttendanceChange("workingHoursPerDay", parsed); }} disabled={!canEdit} {...noSpinner} style={getFieldStyle("attendance", styles.input)} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.workingDaysPerWeek}</label>
            <input type="number" min="1" max="7" value={getAttendanceValue("workingDaysPerWeek")} onChange={(e) => { if (!canEdit) return; const parsed = parseInt(e.target.value, 10); if (!isNaN(parsed)) handleAttendanceChange("workingDaysPerWeek", parsed); }} disabled={!canEdit} {...noSpinner} style={getFieldStyle("attendance", styles.input)} />
          </div>
        </div>

        {/* Weekend Days */}
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{isRTL ? "أيام العطلة الأسبوعية" : "Weekend Days"}</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "8px" }}>
              {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => {
                const isSelected = (getAttendanceValue("weekendDays") || []).includes(day);
                return (
                  <button key={day} type="button" disabled={!canEdit}
                    onClick={() => {
                      if (!canEdit) return;
                      const currentWeekends = getAttendanceValue("weekendDays") || [];
                      const newWeekends = isSelected ? currentWeekends.filter((d) => d !== day) : [...currentWeekends, day];
                      handleAttendanceChange("weekendDays", newWeekends);
                    }}
                    style={{
                      padding: "8px 16px", borderRadius: "8px",
                      border: isSelected ? `2px solid ${PALETTE.primary.base}` : theme === "dark" ? "2px solid rgba(255,255,255,0.2)" : "2px solid rgba(0,0,0,0.15)",
                      background: isSelected ? PALETTE.primary.glow : "transparent",
                      color: isSelected ? PALETTE.primary.base : theme === "dark" ? "#fff" : "#0f172a",
                      fontSize: "13px", fontWeight: isSelected ? "800" : "600",
                      cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.7, transition: "all 0.2s ease",
                    }}
                  >
                    {isRTL ? { Sunday: "الأحد", Monday: "الإثنين", Tuesday: "الثلاثاء", Wednesday: "الأربعاء", Thursday: "الخميس", Friday: "الجمعة", Saturday: "السبت" }[day] : day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete Department Rules ─────────────────────────────────────────── */}
      {config.department && config.departmentRules[config.department] && (
        <div style={{ marginTop: "24px", marginBottom: "24px" }}>
          <button
            onClick={async () => {
              if (window.confirm(
                isRTL
                  ? `هل تريد حذف القواعد الخاصة بقسم ${config.department}؟ سيتم استخدام القواعد العامة بدلاً منها.`
                  : `Delete specific rules for ${config.department}? Company-wide rules will be used instead.`
              )) {
                try {
                  const clientName = profile?.clientName || localStorage.getItem("clientName");
                  const response = await apiFetch(
                    `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(selectedCompanyName)}/departments/${encodeURIComponent(config.department)}`,
                    { method: "DELETE" }
                  );
                  if (response.ok) {
                    setConfig((prev) => {
                      const newRules = { ...prev.departmentRules };
                      delete newRules[prev.department];
                      return { ...prev, departmentRules: newRules, department: "" };
                    });
                    showAlert(isRTL ? "نجاح" : "Success", isRTL ? "تم حذف القواعد الخاصة بالقسم بنجاح" : "Department rules deleted successfully", "success");
                  } else {
                    throw new Error("Failed to delete");
                  }
                } catch (error) {
                  console.error("Error deleting department rules:", error);
                  showAlert(isRTL ? "خطأ" : "Error", isRTL ? "فشل حذف قواعد القسم" : "Failed to delete department rules", "error");
                }
              }
            }}
            style={{ ...styles.removeButton, marginTop: "0px", width: "100%" }}
          >
            <X size={16} />
            {isRTL ? `حذف القواعد الخاصة بقسم ${config.department}` : `Delete ${config.department} Specific Rules`}
          </button>
        </div>
      )}

      {/* ── Absence Deduction ───────────────────────────────────────────────── */}
      <div style={{ ...styles.contentSection, border: styles.contentSection.border, boxShadow: styles.contentSection.boxShadow }}>
        <h3 style={styles.sectionTitle}>
          <div style={styles.sectionIconBadge(PALETTE.blue.light)}>
            <AlertCircle size={20} />
          </div>
          {t.attendance.absenceDeduction}
        </h3>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.absenceDeduction}</label>
            <ReusableDropdown
              items={[
                { value: 0, label: t.attendance.noDeduction },
                { value: 0.25, label: t.attendance.quarterDay },
                { value: 0.5, label: t.attendance.halfDay },
                { value: 1, label: t.attendance.fullDay },
                { value: 1.25, label: t.attendance.oneAndQuarterDay },
                { value: 1.5, label: t.attendance.oneAndHalfDay },
                { value: 2, label: t.attendance.twoDays },
              ]}
              value={getAttendanceValue("absenceDeductionPerDay")}
              onChange={(value) => handleAttendanceChange("absenceDeductionPerDay", parseFloat(value))}
              onSelect={(item) => handleAttendanceChange("absenceDeductionPerDay", parseFloat(item.value))}
              placeholder={isRTL ? "اختر" : "Select"}
              theme={theme} language={language}
              getItemKey={(item) => item.value} getItemLabel={(item) => item.label}
              maxItems={10}
            />
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t.attendance.defaultDailyRatePercentage}</label>
            <p style={{ color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.6)", fontSize: "12px", marginBottom: "8px", lineHeight: "1.5" }}>
              {t.attendance.defaultDailyRatePercentageDesc}
            </p>
            <PermissionInput
              section="attendance" type="number" min="0" max="1" step="0.0001"
              value={getAttendanceValue("defaultDailyRatePercentage") ?? 0}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val >= 0 && val <= 1) handleAttendanceChange("defaultDailyRatePercentage", val);
              }}
              style={getFieldStyle("attendance", styles.input)}
              canEditField={canEditField} getFieldStyle={getFieldStyle}
            />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
           LATE DEDUCTION RULES
      ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ ...styles.contentSection, border: styles.contentSection.border, boxShadow: styles.contentSection.boxShadow }}>
        <h3 style={styles.sectionTitle}>
          <div style={styles.sectionIconBadge(PALETTE.rose.base)}>
            <Clock size={20} />
          </div>
          {t.attendance.lateDeductionRules}
        </h3>

        <p style={{ color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)", fontSize: "14px", marginBottom: "24px", lineHeight: "1.6" }}>
          {isRTL
            ? "حدد نسب الخصم لكل نطاق من دقائق التأخير. كل مرحلة تُطبَّق على نطاق زمني محدد."
            : "Define deduction tiers for different late-minute ranges. Each step covers a specific time window."}
        </p>

        {/* ── Rule Cards ─────────────────────────────────────────────────── */}
        {(getAttendanceValue("lateDeductionRules") || []).map((rule, idx) => {
          const allRules = getAttendanceValue("lateDeductionRules") || [];
          const lateThreshold = allRules[0]?.minutesTo ?? 15;
          const isFirstRule = idx === 0;
          const advanced = isAdvancedExpanded(idx, rule);
          const hasAdvancedValues = ruleHasAdvanced(rule);

          const applyAndNormalize = (newRules) => {
            newRules[0].minutesFrom = 0;
            for (let i = 1; i < newRules.length; i++) {
              newRules[i].minutesFrom = newRules[i - 1].minutesTo ?? 0;
            }
            handleAttendanceChange("lateDeductionRules", newRules);
          };

          const updateRuleField = (field, value) => {
            const newRules = [...(getAttendanceValue("lateDeductionRules") || [])];
            newRules[idx] = { ...newRules[idx], [field]: value };
            handleAttendanceChange("lateDeductionRules", newRules);
          };

          // Per-recurrence deductions: array of 4 nullable values
          const recurrenceDeductions = rule.recurrenceDeductions ?? [null, null, null, null];
          const recurrenceAction = rule.recurrenceAction || "none";
          const recurrenceThreshold = rule.recurrenceThreshold ?? 3;
          const deductionMultiplier = rule.deductionMultiplier ?? 1;
          const recurrenceResetMonths = rule.recurrenceResetMonths ?? null;

          // ── Ordinal labels ──────────────────────────────────────────────
          const ordinalEn = ["1st", "2nd", "3rd", "4th"];
          const ordinalAr = ["الأولى", "الثانية", "الثالثة", "الرابعة"];

          return (
            <div
              key={idx}
              style={{
                position: "relative",
                borderRadius: 12,
                border: `1.5px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                marginBottom: 16,
                overflow: "hidden",
                transition: "box-shadow 0.2s",
              }}
            >
              {/* Blue left accent bar — same for all tiers */}
              <div style={{
                position: "absolute",
                left: isRTL ? undefined : 0,
                right: isRTL ? 0 : undefined,
                top: 0, bottom: 0, width: 4,
                background: blueAccent,
                borderRadius: isRTL ? "0 12px 12px 0" : "12px 0 0 12px",
              }} />

              {/* Card content */}
              <div style={{
                padding: "18px 20px 18px",
                paddingLeft: isRTL ? 20 : 28,
                paddingRight: isRTL ? 28 : 20,
                background: theme === "dark" ? RULE_COLOR.bg : RULE_COLOR.bg,
              }}>
                {/* Step header */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: RULE_COLOR.badge,
                    border: `2px solid ${blueAccent}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800, color: blueAccent, flexShrink: 0,
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: blueAccent }}>
                      {isRTL ? `المستوى ${idx + 1}` : `Tier ${idx + 1}`}
                    </span>
                    <span style={{
                      marginLeft: isRTL ? 0 : 10, marginRight: isRTL ? 10 : 0,
                      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5,
                      background: RULE_COLOR.badge, color: blueAccent,
                    }}>
                      {(rule.minutesFrom ?? 0)}–{rule.minutesTo ?? "∞"} {isRTL ? "دقيقة" : "min"}
                    </span>
                    {hasAdvancedValues && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: "rgba(46,124,186,0.15)", color: blueAccent }}>
                        {isRTL ? "مخصص" : "CUSTOM"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Core fields row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {/* Minutes range */}
                  <div style={styles.formGroup}>
                    {isFirstRule ? (
                      <>
                        <label style={{ ...styles.label, fontWeight: "bold", color: blueAccent }}>
                          {t.attendance.lateThresholdMinutes}
                        </label>
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <input
                            type="number" min="0" step="1"
                            value={rule.minutesTo === null ? "" : rule.minutesTo}
                            onChange={(e) => {
                              const newRules = [...(getAttendanceValue("lateDeductionRules") || [])];
                              const oldThreshold = newRules[0]?.minutesTo ?? 15;
                              const newThreshold = e.target.value === "" ? null : parseInt(e.target.value);
                              newRules[0].minutesTo = newThreshold;
                              for (let i = 1; i < newRules.length; i++) {
                                const currentDelta = (newRules[i].minutesTo ?? 0) - (oldThreshold ?? 0);
                                newRules[i].minutesTo = newThreshold === null ? null : newThreshold + currentDelta;
                              }
                              applyAndNormalize(newRules);
                            }}
                            placeholder={isRTL ? "غير محدود" : "Unlimited"}
                            {...noSpinner}
                            style={{ ...styles.input, fontWeight: "bold", paddingRight: isRTL ? "12px" : "56px", paddingLeft: isRTL ? "56px" : "12px" }}
                            disabled={!canEdit}
                          />
                          <span style={{ position: "absolute", right: isRTL ? undefined : "14px", left: isRTL ? "14px" : undefined, fontSize: "12px", fontWeight: 600, color: blueAccent, pointerEvents: "none" }}>
                            {isRTL ? "د" : "min"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <label style={styles.label}>{t.attendance.additionalMinutes}</label>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ flex: 1 }}>
                            <input
                              type="number" min="0" step="1"
                              value={rule.minutesTo === null ? "" : (rule.minutesTo - lateThreshold)}
                              onChange={(e) => {
                                const newRules = [...(getAttendanceValue("lateDeductionRules") || [])];
                                const deltaMinutes = e.target.value === "" ? null : parseInt(e.target.value);
                                newRules[idx].minutesTo = deltaMinutes === null ? null : lateThreshold + deltaMinutes;
                                applyAndNormalize(newRules);
                              }}
                              placeholder={isRTL ? "غير محدود" : "Unlimited"}
                              {...noSpinner}
                              style={{ ...styles.input }}
                              disabled={!canEdit}
                            />
                          </div>
                          <div style={{ fontSize: "12px", fontWeight: "600", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", whiteSpace: "nowrap" }}>
                            = <span style={{ color: blueAccent, fontWeight: 800 }}>{rule.minutesTo ?? "∞"}{isRTL ? "د" : "m"}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Base deduction % */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t.attendance.deductionPercentage}</label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                      <input
                        type="text"
                        value={rule.deductionPercentage ?? 0}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            const newRules = [...(getAttendanceValue("lateDeductionRules") || [])];
                            newRules[idx].deductionPercentage = val === "" ? "" : parseFloat(val);
                            handleAttendanceChange("lateDeductionRules", newRules);
                          }
                        }}
                        style={{ ...styles.input, paddingRight: isRTL ? "12px" : "40px", paddingLeft: isRTL ? "40px" : "12px" }}
                        disabled={!canEdit}
                      />
                      <span style={{ position: "absolute", right: isRTL ? undefined : "14px", left: isRTL ? "14px" : undefined, fontSize: "16px", fontWeight: "bold", color: blueAccent, pointerEvents: "none" }}>
                        %
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── Advanced Section Toggle ─────────────────────────────── */}
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => toggleAdvanced(idx)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "none", border: "none", cursor: "pointer",
                      padding: "6px 10px", borderRadius: 8,
                      fontSize: 12, fontWeight: 700,
                      color: advanced
                        ? theme === "dark" ? "#93c5fd" : blueAccent
                        : theme === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
                      background: advanced
                        ? theme === "dark" ? "rgba(46,124,186,0.12)" : "rgba(46,124,186,0.08)"
                        : "transparent",
                      transition: "all 0.18s ease",
                    }}
                  >
                    {advanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {isRTL ? "⚙️ الإعدادات المتقدمة (التكرار)" : "⚙️ Advanced (Recurrence)"}
                    {hasAdvancedValues && !advanced && (
                      <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "rgba(46,124,186,0.2)", color: blueAccent, marginLeft: 4 }}>
                        {isRTL ? "مُعدَّل" : "MODIFIED"}
                      </span>
                    )}
                  </button>

                  {/* ── Advanced Panel ──────────────────────────────────── */}
                  {advanced && (
                    <div style={{
                      marginTop: 12, padding: "18px 18px 20px", borderRadius: 10,
                      background: theme === "dark" ? "rgba(46,124,186,0.07)" : "rgba(46,124,186,0.05)",
                      border: `1.5px solid ${theme === "dark" ? "rgba(46,124,186,0.2)" : "rgba(46,124,186,0.18)"}`,
                    }}>

                      {/* ── Section header ─────────────────────────────── */}
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 18 }}>
                        <Repeat size={14} color={blueAccent} />
                        <span style={{ fontSize: 12, fontWeight: 800, color: theme === "dark" ? "#93c5fd" : blueAccent, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          {isRTL ? "سلوك التكرار داخل دورة الرواتب" : "Recurrence Behavior (within payroll cycle)"}
                        </span>
                      </div>

                      {/* ══ PRIMARY: Multiplier + Threshold + Action ══════ */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                        {/* Deduction Multiplier — PRIMARY */}
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 6 }}>
                            <Zap size={13} color={blueAccent} />
                            {isRTL ? "مضاعف الخصم" : "Deduction Multiplier"}
                          </label>
                          <p style={{ fontSize: 11, opacity: 0.65, marginBottom: 6, lineHeight: 1.4 }}>
                            {isRTL
                              ? "يُضاعف الخصم أسيًا بهذا المعامل لكل تكرار بعد الحد (مثلاً 1.5^n). افتراضي: 1 = بدون تغيير. استخدم قيم قريبة من 1 (1.1–1.3) للتصعيد التدريجي"
                              : "Deduction compounds exponentially by this factor for each occurrence past the threshold (e.g., 1.5^n). Default: 1 = no change. Use values near 1 (1.1–1.3) for gentle escalation"}
                          </p>
                          <input
                            type="number" min={1} step={0.5} value={deductionMultiplier}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val >= 1) updateRuleField("deductionMultiplier", val);
                            }}
                            disabled={!canEdit} {...noSpinner} style={{ ...styles.input }}
                          />
                        </div>

                        {/* Recurrence Threshold */}
                        <div style={styles.formGroup}>
                          <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 6 }}>
                            <AlertTriangle size={13} color={blueAccent} />
                            {isRTL ? "حد التكرار" : "Recurrence Threshold"}
                          </label>
                          <p style={{ fontSize: 11, opacity: 0.65, marginBottom: 6, lineHeight: 1.4 }}>
                            {isRTL
                              ? "عدد مرات التأخير التي تُفعّل إجراء التكرار"
                              : "Number of occurrences that triggers the recurrence action"}
                          </p>
                          <input
                            type="number" min={1} step={1} value={recurrenceThreshold}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && val >= 1) updateRuleField("recurrenceThreshold", val);
                            }}
                            disabled={!canEdit} {...noSpinner} style={{ ...styles.input }}
                          />
                        </div>

                        {/* Recurrence Action — full width */}
                        <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                          <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 6 }}>
                            <Shield size={13} color={blueAccent} />
                            {isRTL ? "إجراء التكرار" : "Recurrence Action"}
                          </label>
                          <p style={{ fontSize: 11, opacity: 0.65, marginBottom: 6, lineHeight: 1.4 }}>
                            {isRTL
                              ? "ما الذي يحدث عند تجاوز حد التكرار في نفس الدورة؟"
                              : "What happens when recurrence threshold is crossed in the same cycle?"}
                          </p>
                          <ReusableDropdown
                            items={RECURRENCE_ACTIONS.map((a) => ({ value: a.value, label: isRTL ? a.labelAr : a.labelEn }))}
                            value={recurrenceAction}
                            onChange={(val) => updateRuleField("recurrenceAction", val)}
                            onSelect={(item) => updateRuleField("recurrenceAction", item.value)}
                            theme={theme} language={language}
                            getItemKey={(item) => item.value} getItemLabel={(item) => item.label}
                            disabled={!canEdit}
                            placeholder={isRTL ? "اختر إجراء..." : "Select action..."}
                            maxItems={10}
                          />
                        </div>

                        {/* Fixed Penalty Amount — only for fixed_penalty */}
                        {recurrenceAction === "fixed_penalty" && (
                          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                            <label style={{ ...styles.label, display: "flex", alignItems: "center", gap: 6 }}>
                              <AlertCircle size={13} color={blueAccent} />
                              {isRTL ? "مبلغ الغرامة الثابتة (% من الأجر اليومي)" : "Fixed Penalty Amount (% of daily rate)"}
                            </label>
                            <p style={{ fontSize: 11, opacity: 0.65, marginBottom: 6, lineHeight: 1.4 }}>
                              {isRTL
                                ? "نسبة مئوية من الأجر اليومي تُطبَّق كغرامة ثابتة بعد تجاوز الحد"
                                : "Percentage of daily rate applied as a fixed penalty after crossing the threshold"}
                            </p>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                              <input
                                type="number" min={0} step={0.5}
                                value={rule.fixedPenaltyAmount ?? 0}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  if (!isNaN(val) && val >= 0) updateRuleField("fixedPenaltyAmount", val);
                                }}
                                disabled={!canEdit}
                                {...noSpinner}
                                style={{ ...styles.input, paddingRight: isRTL ? "12px" : "40px", paddingLeft: isRTL ? "40px" : "12px" }}
                              />
                              <span style={{ position: "absolute", right: isRTL ? undefined : "14px", left: isRTL ? "14px" : undefined, fontSize: "16px", fontWeight: "bold", color: blueAccent, pointerEvents: "none" }}>
                                %
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ══ OPTIONAL: Per-occurrence custom deductions (1st–4th) ══ */}
                      {(() => {
                        const hasCustomDeductions = recurrenceDeductions.some((v) => v !== null && v !== undefined);
                        const perOccKey = `perOcc_${idx}`;
                        const perOccOpen = expandedAdvanced[perOccKey] !== undefined
                          ? expandedAdvanced[perOccKey]
                          : hasCustomDeductions;
                        return (
                          <div style={{ marginBottom: 16 }}>
                            {/* Toggle button */}
                            <button
                              type="button"
                              onClick={() => setExpandedAdvanced((prev) => ({ ...prev, [perOccKey]: !perOccOpen }))}
                              style={{
                                display: "flex", alignItems: "center", gap: 7,
                                width: "100%", background: "none", border: `1px dashed ${theme === "dark" ? "rgba(46,124,186,0.3)" : "rgba(46,124,186,0.25)"}`,
                                borderRadius: 8, cursor: "pointer", padding: "8px 12px",
                                color: theme === "dark" ? "rgba(147,197,253,0.8)" : "rgba(46,124,186,0.8)",
                                fontSize: 12, fontWeight: 600, transition: "all 0.15s ease",
                              }}
                            >
                              {perOccOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                              <Zap size={12} color={blueAccent} style={{ opacity: 0.7 }} />
                              <span style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                                {isRTL
                                  ? "تخصيص نسبة خصم مختلفة لكل تكرار (اختياري)"
                                  : "Set different deduction % per occurrence — optional (1st–4th)"}
                              </span>
                              {hasCustomDeductions && (
                                <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "rgba(46,124,186,0.18)", color: blueAccent }}>
                                  {isRTL ? "مُعدَّل" : "CUSTOM"}
                                </span>
                              )}
                            </button>

                            {/* Collapsible content */}
                            {perOccOpen && (
                              <div style={{
                                marginTop: 10, padding: "14px 16px", borderRadius: 8,
                                background: theme === "dark" ? "rgba(46,124,186,0.06)" : "rgba(46,124,186,0.04)",
                                border: `1px solid ${theme === "dark" ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.12)"}`,
                              }}>
                                <p style={{ fontSize: 11, opacity: 0.6, marginBottom: 12, lineHeight: 1.5 }}>
                                  {isRTL
                                    ? "اتركها فارغة لاستخدام مضاعف الخصم الأساسي. تُطبَّق هذه النسب بدلاً من المضاعف إذا كانت محددة."
                                    : "Leave blank to fall back to the multiplier above. These override the multiplier when set."}
                                </p>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(140px, 100%), 1fr))", gap: 10 }}>
                                  {[0, 1, 2, 3].map((occIdx) => {
                                    const currentVal = recurrenceDeductions[occIdx];
                                    return (
                                      <div key={occIdx} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <label style={{ fontSize: 11, fontWeight: 700, color: theme === "dark" ? "#93c5fd" : blueAccent, textAlign: "center" }}>
                                          {isRTL ? ordinalAr[occIdx] : ordinalEn[occIdx]}
                                        </label>
                                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                          <input
                                            type="number" min={0} step={0.5}
                                            placeholder={String(rule.deductionPercentage ?? 0)}
                                            value={currentVal === null || currentVal === undefined ? "" : currentVal}
                                            onChange={(e) => {
                                              if (!canEdit) return;
                                              const newDeductions = [...recurrenceDeductions];
                                              newDeductions[occIdx] = e.target.value === "" ? null : parseFloat(e.target.value);
                                              updateRuleField("recurrenceDeductions", newDeductions);
                                            }}
                                            disabled={!canEdit}
                                            {...noSpinner}
                                            style={{
                                              ...styles.input,
                                              textAlign: "center",
                                              paddingRight: "22px",
                                              paddingLeft: "6px",
                                              fontSize: 13,
                                              border: currentVal !== null && currentVal !== undefined
                                                ? `1.5px solid ${blueAccent}`
                                                : undefined,
                                            }}
                                          />
                                          <span style={{ position: "absolute", right: 7, fontSize: 12, fontWeight: 700, color: blueAccent, pointerEvents: "none" }}>%</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Clear all button */}
                                {hasCustomDeductions && (
                                  <button
                                    type="button"
                                    disabled={!canEdit}
                                    onClick={() => updateRuleField("recurrenceDeductions", [null, null, null, null])}
                                    style={{
                                      marginTop: 10, display: "flex", alignItems: "center", gap: 5,
                                      background: "none", border: "none", cursor: canEdit ? "pointer" : "not-allowed",
                                      fontSize: 11, fontWeight: 600, padding: "4px 0",
                                      color: theme === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                                    }}
                                  >
                                    <X size={11} />
                                    {isRTL ? "مسح جميع القيم المخصصة" : "Clear all custom values"}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ══ 3. Recurrence Reset ══════════════════════════ */}
                      <div style={{
                        marginTop: 18, padding: "14px 16px", borderRadius: 8,
                        background: theme === "dark" ? "rgba(46,124,186,0.08)" : "rgba(46,124,186,0.06)",
                        border: `1px solid ${theme === "dark" ? "rgba(46,124,186,0.18)" : "rgba(46,124,186,0.14)"}`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                          <RefreshCw size={13} color={blueAccent} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: theme === "dark" ? "#93c5fd" : blueAccent }}>
                            {isRTL ? "إعادة تعيين التكرار" : "Recurrence Reset"}
                          </span>
                          {recurrenceResetMonths !== null && recurrenceResetMonths !== undefined && (
                            <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 4, background: "rgba(46,124,186,0.2)", color: blueAccent, marginLeft: 6 }}>
                              {isRTL ? "مفعّل" : "ACTIVE"}
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: 11, opacity: 0.65, marginBottom: 12, lineHeight: 1.5 }}>
                          {isRTL
                            ? "بعد كم شهر تُعاد تهيئة عداد التكرار لهذا المستوى؟ اتركه فارغاً لإعادة التعيين في كل دورة راتب فقط."
                            : "After how many months should the recurrence counter reset for this tier? Leave blank to reset only each payroll cycle."}
                        </p>

                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ position: "relative", display: "flex", alignItems: "center", width: 160 }}>
                            <input
                              type="number" min={1} step={1}
                              placeholder={isRTL ? "فارغ = كل دورة" : "blank = each cycle"}
                              value={recurrenceResetMonths === null || recurrenceResetMonths === undefined ? "" : recurrenceResetMonths}
                              onChange={(e) => {
                                if (!canEdit) return;
                                const raw = e.target.value;
                                if (raw === "") { updateRuleField("recurrenceResetMonths", null); return; }
                                const n = parseInt(raw, 10);
                                if (!isNaN(n) && n >= 1) updateRuleField("recurrenceResetMonths", n);
                              }}
                              disabled={!canEdit}
                              {...noSpinner}
                              style={{
                                ...styles.input,
                                paddingRight: isRTL ? "12px" : "64px",
                                paddingLeft: isRTL ? "64px" : "12px",
                                border: recurrenceResetMonths !== null && recurrenceResetMonths !== undefined
                                  ? `1.5px solid ${blueAccent}`
                                  : undefined,
                              }}
                            />
                            <span style={{
                              position: "absolute",
                              right: isRTL ? undefined : "10px",
                              left: isRTL ? "10px" : undefined,
                              fontSize: 11, fontWeight: 600,
                              color: blueAccent, pointerEvents: "none", whiteSpace: "nowrap",
                            }}>
                              {isRTL ? "شهر" : "month(s)"}
                            </span>
                          </div>

                          {/* Quick-select buttons */}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {[1, 3, 6, 12].map((m) => (
                              <button
                                key={m}
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateRuleField("recurrenceResetMonths", recurrenceResetMonths === m ? null : m)}
                                style={{
                                  padding: "5px 11px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                                  cursor: canEdit ? "pointer" : "not-allowed",
                                  border: `1.5px solid ${recurrenceResetMonths === m ? blueAccent : theme === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"}`,
                                  background: recurrenceResetMonths === m
                                    ? theme === "dark" ? "rgba(46,124,186,0.25)" : "rgba(46,124,186,0.12)"
                                    : "transparent",
                                  color: recurrenceResetMonths === m ? blueAccent : theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                {m}{isRTL ? "ش" : "m"}
                              </button>
                            ))}
                          </div>

                          {/* Clear reset button */}
                          {recurrenceResetMonths !== null && recurrenceResetMonths !== undefined && (
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => updateRuleField("recurrenceResetMonths", null)}
                              title={isRTL ? "إزالة الإعداد" : "Clear reset setting"}
                              style={{
                                padding: "5px 8px", borderRadius: 6, fontSize: 12,
                                cursor: canEdit ? "pointer" : "not-allowed",
                                border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)"}`,
                                background: "transparent",
                                color: theme === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)",
                                display: "flex", alignItems: "center", gap: 4,
                              }}
                            >
                              <X size={12} />
                              {isRTL ? "مسح" : "Clear"}
                            </button>
                          )}
                        </div>
                      </div>
                      {/* ─────────────────────────────────────────────── */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* ── Add / Remove buttons ──────────────────────────────────────── */}
        <div style={{
          display: "flex", gap: "12px",
          justifyContent: isRTL ? "flex-end" : "flex-start",
          flexWrap: "wrap", alignItems: "center", marginTop: 4,
        }}>
          <button
            onClick={() => {
              const newRules = [...(getAttendanceValue("lateDeductionRules") || [])];
              const lastRule = newRules[newRules.length - 1];
              newRules.push({
                minutesFrom: lastRule.minutesTo ?? 0,
                minutesTo: null,
                deductionPercentage: 0,
                deductionMultiplier: 1,
                recurrenceAction: "none",
                recurrenceThreshold: 3,
                recurrenceDeductions: [null, null, null, null],
                recurrenceResetMonths: null,
              });
              newRules[0].minutesFrom = 0;
              for (let i = 1; i < newRules.length; i++) {
                newRules[i].minutesFrom = newRules[i - 1].minutesTo ?? 0;
              }
              handleAttendanceChange("lateDeductionRules", newRules);
            }}
            style={styles.addButton}
            disabled={!canEdit}
          >
            <Plus size={18} />
            {t.attendance.addRule}
          </button>

          {(getAttendanceValue("lateDeductionRules") || []).length > 1 && (
            <button
              onClick={() => {
                const newRules = (getAttendanceValue("lateDeductionRules") || []).slice(0, -1);
                newRules[newRules.length - 1].minutesTo = null;
                handleAttendanceChange("lateDeductionRules", newRules);
                setExpandedAdvanced((prev) => {
                  const next = { ...prev };
                  delete next[(getAttendanceValue("lateDeductionRules") || []).length - 1];
                  return next;
                });
              }}
              style={styles.removeButton}
              disabled={!canEdit}
            >
              <X size={16} />
              {t.attendance.removeRule}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AttendanceTab;